import {Status} from '@muvio/honda-offers-builder/model'

import {MapitDevice, PhysicalState} from '@/types/dynamodb-types'

export class RegistrationHelper {
  public canDeviceBeInstalled(device?: MapitDevice | null): CanDeviceBeInstalledResult {
    if (device == null) {
      return {
        canBeInstalled: false,
        reason: PreventInstallReason.NotFound,
      }
    } else if (device.vehicle?.id != null) {
      return {
        canBeInstalled: false,
        reason: PreventInstallReason.AlreadyAttached,
      }
    } else if (device.physicalState && device.physicalState !== PhysicalState.NotInstalled) {
      return {
        canBeInstalled: false,
        reason: PreventInstallReason.NotInstallable,
      }
    } else if (device.heldByPendingRegistration) {
      // Reserved by a registration that has not been completed yet: the device is not attached to a
      // vehicle, so only the backend knows it is taken. See MPR-3534.
      return {
        canBeInstalled: false,
        reason: PreventInstallReason.PendingRegistration,
      }
    }
    return {
      canBeInstalled: true,
    }
  }
}

export enum PreventInstallReason {
  AlreadyAttached = 'ALREADY_ATTACHED',
  NotInstallable = 'NOT_INSTALLABLE',
  NotFound = 'NOT_FOUND',
  PendingRegistration = 'PENDING_REGISTRATION',
}

interface CanDeviceBeInstalledResult {
  canBeInstalled: boolean
  reason?: PreventInstallReason
}

const DEVICE_CONFLICT_CODE = 'RegDevUniquenessViolationError'

/**
 * Reason behind a failed registration creation, when it is one the IMEI check already models: the
 * backend answers 409 for a device held by another registration that is still pending, which the
 * dealer only hits when it is taken between the check and the submit. See MPR-3534.
 */
export function deviceConflictReason(error: unknown): PreventInstallReason | undefined {
  const response = (error as {response?: {status?: number; data?: {code?: string}}})?.response

  return response?.status === 409 && response?.data?.code === DEVICE_CONFLICT_CODE
    ? PreventInstallReason.PendingRegistration
    : undefined
}

export const STATES_ALLOWED_FOR_REGISTRATION = [
  Status.Accepted,
  Status.COICreated,
  Status.Quote,
  Status.Ordered,
  Status.Invoiced,
  Status.Completed,
]

export function isRegistrationAllowed(status: Status) {
  return STATES_ALLOWED_FOR_REGISTRATION.includes(status)
}
